// JAWS vs VoiceOver (see doc/jaws-voiceover-accessibility-reference.md):
// Required-field errors use role="alert" on the inline message (not #aria_assertive too).
// aria-describedby links the h1 to visible intro copy; VO may concatenate differently.

import React, { useEffect, useRef, useState } from 'react'
import { Box } from '../_atoms/index'
import useTranslation from '../shared/TranslationProvider'
import OnboardingResumeUpload from '../OnboardingSteps/OnboardingResumeUpload'
import parse from 'html-react-parser'
import { SpinnerWhite } from '../shared/Spinner'
import { useSelectOnboarding } from './SelectOnboardingProvider'
import { Input } from '~/lib/ui/input'
import { Button } from '~/lib/ui/button'
import OnboardingFormLabel from './OnboardingFormLabel'

const WelcomePage = () => {
  const { t } = useTranslation('SelectOnboarding')
  const { firstName, updateOnboardingMutation } = useSelectOnboarding()
  const [isLoading, setIsLoading] = useState(false)
  const [linkedInUrl, setLinkedInUrl] = useState('https://www.linkedin.com/in/')
  const [targetRole, setTargetRole] = useState('')
  const [showError, setShowError] = useState(false)
  const targetRoleRef = useRef(null)
  const targetRoleId = 'select-onboarding-target-role'
  const targetRoleErrorId = 'select-onboarding-target-role-error'
  const welcomeDescriptionId = 'select-onboarding-welcome-description'

  const handleContinueClick = () => {
    if (!targetRole.trim()) {
      setShowError(true)
      targetRoleRef.current?.focus()
      return
    }
    const values = { linked_in_url: linkedInUrl, target_role: targetRole }
    setIsLoading(true)
    updateOnboardingMutation.mutate(values)
  }

  if (!firstName) return null
  return (
    <div className="bg-select-onboarding-gradient pb-8 pt-1">
      <Box className="mx-4 mt-8 flex max-w-3xl flex-col flex-wrap content-center rounded-[20px] pt-10 md:mx-auto">
        <Box>
          <h1
            data-onboarding-step-heading
            tabIndex={-1}
            aria-describedby={welcomeDescriptionId}
            className="mb-[10px] focus:outline-none"
          >
            {t('welcome', { firstName })}
          </h1>
          <div id={welcomeDescriptionId}>{parse(t('welcome_description'))}</div>
        </Box>
        <Box className="pt-0">
          <OnboardingFormLabel htmlFor={targetRoleId}>
            {t('target_role_label')}
          </OnboardingFormLabel>
          <Input
            id={targetRoleId}
            ref={targetRoleRef}
            value={targetRole}
            onChange={(e) => {
              setTargetRole(e.target.value)
              if (showError) setShowError(false)
            }}
            aria-required="true"
            aria-invalid={showError}
            aria-describedby={showError ? targetRoleErrorId : undefined}
          />
          {showError && (
            <span
              id={targetRoleErrorId}
              role="alert"
              className="text-sm text-red-500"
            >
              {t('target_role_error')}
            </span>
          )}
        </Box>
        <Box className="pt-0">
          <LinkedInFormField
            linkedInUrl={linkedInUrl}
            setLinkedInUrl={setLinkedInUrl}
          />
        </Box>
        <Box className="pt-0">
          <OnboardingResumeUpload
            label={t('resume.label')}
            subtext={t('resume.subtext')}
            selectOnboarding
          />
        </Box>
        <Box className="flex flex-col">
          <Button
            className="mb-4 flex items-center gap-2 md:mb-0 md:self-end"
            onClick={handleContinueClick}
          >
            {t('continue')}
            <SpinnerWhite show={isLoading} />
          </Button>
        </Box>
      </Box>
    </div>
  )
}

const LinkedInFormField = ({ linkedInUrl, setLinkedInUrl }) => {
  const { t } = useTranslation('SelectOnboarding')
  const linkedInId = 'select-onboarding-linked-in'
  useEffect(() => {
    const origUrl = linkedInUrl.trim()
    if (!origUrl) return
    let url = ''

    if (origUrl.startsWith('https://')) url = origUrl
    else if (origUrl.startsWith('www.linkedin.com/in/'))
      url = 'https://' + origUrl
    else if (origUrl.startsWith('linkedin.com/in/'))
      url = 'https://www.' + origUrl
    else url = origUrl
    if (url && !url.startsWith('https://'))
      url = 'https://www.linkedin.com/in/' + url
    if (url === linkedInUrl) return
    setLinkedInUrl(url)
  }, [linkedInUrl, setLinkedInUrl])
  return (
    <>
      <OnboardingFormLabel htmlFor={linkedInId}>
        {t('linked_in.label')}
      </OnboardingFormLabel>
      <Input
        id={linkedInId}
        value={linkedInUrl}
        onChange={(e) => setLinkedInUrl(e.target.value)}
      />
    </>
  )
}

export default WelcomePage
